import { type ChangeEvent, type ReactNode, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";
import type { MutationFunctionOptions } from "@apollo/client";
import { Box, Stack, type SxProps } from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { CustomButton } from "../DataTable";
import { ErrorMessage } from "../ErrorMessage";
import { usePaginationParams, useUpdateQueryParams } from "@/shared/hooks";
import {
  getErrorMessage,
  getGraphQLErrorCode,
} from "@/shared/utils/errorCodes";

const DefaultTexts = {
  createItem: "Создать",
  deleteItems: "Удалить",
  deleteItemsSuccess: "Успешно удалено",
  deleteItemsError: "Не удалось удалить",
  restoreItems: "Восстановить",
  restoreItemsSuccess: "Успешно восстановлено",
  restoreItemsError: "Не удалось восстановить",
  labelRowsPerPage: "Строк на странице",
};

type DeleteOrRestoreMutationFn = (
  options?: MutationFunctionOptions<any, { ids: string[] }>
) => void;

export interface DataListViewData<T> {
  count: number;
  nodes: T[];
}

export interface DataListViewColumn<T> {
  title: string;
  sx?: SxProps;
  renderCell: (data: T) => ReactNode;
}

export type DataListViewColumns<T> = Record<string, DataListViewColumn<T>>;

export interface DataListViewProps<T extends { id: string }> {
  data?: DataListViewData<T>;
  columns: DataListViewColumns<T>;
  onSelectionChange?: (selectedIds: string[]) => void;
  deleteItems?: DeleteOrRestoreMutationFn;
  restoreItems?: DeleteOrRestoreMutationFn;
  refetch: () => Promise<unknown>;
  createLink?: string;
  onCreate?: () => void;
  createButton?: ReactNode;
  headButtons?: ReactNode | ((selectedIds: string[]) => ReactNode);
  texts?: Partial<typeof DefaultTexts>;
  isLoading?: boolean;
  error?: unknown;
  isHideHeader?: boolean;
  isPagination?: boolean;
  /** Какие строки вообще можно выбирать; остальные показываются без чекбокса */
  hasShowSelect?: (row: T) => boolean;
  selectableRows?: boolean;
  deleteButton?:
    | ReactNode
    | ((selectedIds: string[], onClearSelection?: () => void) => ReactNode);
  selectableComponent?:
    | ReactNode
    | ((selectedIds: string[], onClearSelection?: () => void) => ReactNode);
}

export function DataListView<T extends { id: string }>({
  columns,
  onSelectionChange,
  deleteItems,
  restoreItems,
  refetch,
  createLink,
  onCreate,
  createButton,
  headButtons,
  texts,
  error,
  data = { count: 0, nodes: [] },
  isLoading = false,
  isHideHeader = false,
  isPagination = true,
  hasShowSelect,
  selectableRows = false,
  deleteButton,
  selectableComponent,
}: DataListViewProps<T>) {
  const [, updateSearchQuery] = useUpdateQueryParams();
  const { page, rowsPerPage } = usePaginationParams();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { nodes, count } = data;

  const isSelectable = (row: T) => !hasShowSelect || hasShowSelect(row);
  const selectableNodes = nodes.filter(isSelectable);
  const showCheckboxes = selectableRows && selectableNodes.length > 0;

  // Выбор не должен переживать смену страницы или фильтра
  useEffect(() => {
    const idsOnPage = new Set(nodes.map((row) => row.id));
    setSelectedIds((prev) => prev.filter((id) => idsOnPage.has(id)));
  }, [nodes]);

  useEffect(() => {
    onSelectionChange?.(selectedIds);
  }, [selectedIds, onSelectionChange]);

  // Выбранными считаем только те строки, которые вообще разрешено выбирать
  const effectiveSelectedIds = selectedIds.filter((id) => {
    const row = nodes.find((node) => node.id === id);
    return row && isSelectable(row);
  });

  const onChangeAllCheckbox = () => {
    setSelectedIds((prev) =>
      prev.length === selectableNodes.length
        ? []
        : selectableNodes.map((row) => row.id)
    );
  };

  const onChangeRowCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    rowId: string
  ) => {
    const row = nodes.find((node) => node.id === rowId);
    if (!row || !isSelectable(row)) return;

    setSelectedIds((prev) =>
      event.target.checked
        ? prev.includes(rowId)
          ? prev
          : [...prev, rowId]
        : prev.filter((id) => id !== rowId)
    );
  };

  const handleRestoreItems = () => {
    if (!restoreItems) return;

    restoreItems({
      variables: { ids: effectiveSelectedIds },
      onError(error) {
        enqueueSnackbar(
          getErrorMessage(
            getGraphQLErrorCode(error),
            error.message ??
              texts?.restoreItemsError ??
              DefaultTexts.restoreItemsError
          ),
          { variant: "error" }
        );
      },
      onCompleted() {
        enqueueSnackbar(
          texts?.restoreItemsSuccess ?? DefaultTexts.restoreItemsSuccess,
          { variant: "success" }
        );
        refetch();
      },
    });
  };

  const handleDeleteItems = () => {
    if (!deleteItems) return;

    deleteItems({
      variables: { ids: effectiveSelectedIds },
      onError(error) {
        enqueueSnackbar(
          getErrorMessage(
            getGraphQLErrorCode(error),
            error.message ??
              texts?.deleteItemsError ??
              DefaultTexts.deleteItemsError
          ),
          { variant: "error" }
        );
      },
      onCompleted() {
        enqueueSnackbar(
          texts?.deleteItemsSuccess ?? DefaultTexts.deleteItemsSuccess,
          {
            variant: "success",
            action: () =>
              restoreItems ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRestoreItems}
                >
                  Отмена
                </Button>
              ) : null,
          }
        );
        refetch();
      },
    });
  };

  if (error) return <ErrorMessage error={error} />;

  const columnEntries = Object.entries(columns);

  const renderSlot = (
    slot:
      | ReactNode
      | ((selectedIds: string[], onClearSelection?: () => void) => ReactNode)
  ) =>
    typeof slot === "function"
      ? slot(effectiveSelectedIds, () => setSelectedIds([]))
      : slot;

  const hasSelection = effectiveSelectedIds.length > 0;

  return (
    <>
      {isLoading && <LinearProgress />}

      <Stack sx={{ my: 2, overflow: "auto" }}>
        {!isHideHeader && (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">
              {hasSelection
                ? `${effectiveSelectedIds.length} выбрано`
                : "Список"}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              {typeof headButtons === "function"
                ? headButtons(effectiveSelectedIds)
                : headButtons}

              {hasSelection && (deleteItems || selectableRows) ? (
                selectableComponent ? (
                  renderSlot(selectableComponent)
                ) : deleteButton ? (
                  renderSlot(deleteButton)
                ) : (
                  <CustomButton
                    icon={DeleteIcon}
                    text={texts?.deleteItems ?? DefaultTexts.deleteItems}
                    onClick={handleDeleteItems}
                  />
                )
              ) : createButton ? (
                createButton
              ) : (
                (createLink || onCreate) && (
                  <CustomButton
                    icon={AddIcon}
                    text={texts?.createItem ?? DefaultTexts.createItem}
                    sx={{
                      backgroundColor: "primary.main",
                      color: "common.white",
                      "&:hover": { backgroundColor: "primary.dark" },
                    }}
                    {...(createLink
                      ? { component: Link, to: createLink }
                      : { onClick: onCreate })}
                  />
                )
              )}
            </Stack>
          </Stack>
        )}

        <Stack direction="row" spacing={2} alignItems="center" mb={1}>
          {showCheckboxes && (
            <Stack sx={{ width: 20 }}>
              <Checkbox
                color="default"
                indeterminate={
                  effectiveSelectedIds.length > 0 &&
                  effectiveSelectedIds.length !== selectableNodes.length
                }
                checked={
                  selectableNodes.length > 0 &&
                  effectiveSelectedIds.length === selectableNodes.length
                }
                onChange={onChangeAllCheckbox}
              />
            </Stack>
          )}

          {columnEntries.map(([key, column]) => (
            <Stack key={key} sx={column.sx}>
              <Typography variant="subtitle2">{column.title}</Typography>
            </Stack>
          ))}
        </Stack>

        {nodes.length > 0 ? (
          <Stack spacing={2}>
            {nodes.map((row) => (
              <Stack
                key={row.id}
                direction="row"
                spacing={2}
                alignItems="center"
                sx={({ palette }) => ({
                  pt: 2,
                  borderTop: `1px solid ${palette.divider}`,
                })}
              >
                {showCheckboxes && (
                  <Stack sx={{ width: 20 }}>
                    <Checkbox
                      color="default"
                      checked={selectedIds.includes(row.id)}
                      disabled={!isSelectable(row)}
                      sx={{ opacity: isSelectable(row) ? 1 : 0 }}
                      onChange={(event) => onChangeRowCheckbox(event, row.id)}
                    />
                  </Stack>
                )}

                {columnEntries.map(([key, column]) => (
                  <Stack key={key} sx={column.sx}>
                    {column.renderCell(row)}
                  </Stack>
                ))}
              </Stack>
            ))}
          </Stack>
        ) : (
          !isLoading && (
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign="center"
              m={2}
            >
              Ничего не найдено
            </Typography>
          )
        )}
      </Stack>

      {count > rowsPerPage && isPagination && (
        <TablePagination
          component={Box}
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) =>
            updateSearchQuery({ page: newPage || 0 })
          }
          onRowsPerPageChange={(event) =>
            updateSearchQuery({
              page: 0,
              rowsPerPage: Number(event.target.value) || 10,
            })
          }
          labelRowsPerPage={`${texts?.labelRowsPerPage ?? DefaultTexts.labelRowsPerPage}:`}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} из ${count}`
          }
          slotProps={{
            toolbar: { sx: { justifyContent: "center" } },
            spacer: { sx: { display: "none" } },
            selectLabel: { sx: { display: { xs: "none", sm: "inline-flex" } } },
            select: { sx: { display: { xs: "none", sm: "inline-flex" } } },
          }}
        />
      )}
    </>
  );
}
